import cx from 'classnames'
import _ from 'lodash'
import { createRef, useState } from 'react'

import http from '../../../lib/http'
import MagicGrid from '../MagicGrid'
import {
  ErrorMessage,
  Pagination,
  TextBox
} from '../NarusMaterial'

import styles from './index.module.scss'

const QUERY = {
  count: 0,
  timeout: 500
}

const PER_PAGE = 50

const SearchGrid = ({
  label,
  columns,
  tags,
  url,
  sort,
  expand,
  filter,
  onResponse
}) => {
  const groups = _.values(_.groupBy(tags, 'group'))
  const exclusive = groups.reduce((acc, group) => {
    const values = group.map(t => t.value)
    values.forEach(value => {
      acc[value] = values
    })
    return acc
  }, {})
  const [ records, setRecords ] = useState({ filter })
  const ref = createRef()
  const schema = columns.reduce((acc, column) => {
    acc.headers.push({
      title: column.title,
      displayFn: column.display,
      sortBy: column.sortBy,
      performSort: false
    })
    acc.sort.push(column.sort)
    return acc
  }, {
    headers: [],
    sort: []
  })
  const loadRecords = (page, on, dir, filter) => {
    const params = {
      count: PER_PAGE,
      page,
      dir,
      on: schema.sort[on],
      ...filter,
      tags: filter?.tags?.join(',')
    }
    setRecords({
      loading: true,
      filter
    })
    return http
      .getRequest(url, params)
      .then(response => {
        let { data, count, total } = response

        setRecords(current => {
          return {
            data,
            paging: {
              count,
              total,
              page
            },
            sort: {
              on,
              dir
            },
            filter: current?.filter
          }
        })

        onResponse?.(response)
      })
      .catch(error => setRecords({ error: error.toString() }))
  }
  const onLoad = _.debounce((filter, count) => {
    if (QUERY.count === count) {
      ref?.current?.collapse()
      loadRecords(0, records?.sort?.on, records?.sort?.dir, filter)
    }
  }, QUERY.timeout)
  const doSearch = ({ search, tags }) => {
    QUERY.count += 1
    const filter = {
      ...records?.filter,
      search,
      tags
    }
    setRecords({
      loading: true,
      filter
    })
    onLoad(filter, QUERY.count)
  }
  const onSearch = search => doSearch({ search, tags: records?.filter?.tags })
  const onTag = tag => {
    const search = records?.filter?.search
    if (records?.filter?.tags?.includes(tag)) {
      doSearch({
        search,
        tags: records?.filter?.tags?.filter(t => t !==
          tag)
      })
    } else {
      const values = exclusive[tag]
      doSearch({
        search,
        tags: (records?.filter?.tags?.filter(t => !values?.includes(t)) || [])
          .concat(tag)
      })
    }
  }
  const onClear = () => doSearch({ tags: records?.filter?.tags })
  const onSort = value => {
    ref?.current?.collapse()
    loadRecords(0, value.index, value.direction, records?.filter)
  }
  const onPagination = value => {
    ref?.current?.collapse()
    loadRecords(
      value - 1,
      records?.sort?.on,
      records?.sort?.dir,
      records?.filter
    )
  }

  let currentPage = records?.paging?.page ?? 0
  let total = records?.paging?.total ?? 0
  let pages = Math.ceil(total / PER_PAGE) || 0

  const rows = records?.data?.map(row => ({
    ...row,
    component: expand?.(row)
  })) || []

  return (
    <div>
      <TextBox
        name='search'
        value={records?.filter?.search}
        label={label}
        onChange={(event) => onSearch(event.target.value)}
        onClear={onClear}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '40px'
        }}
      >
        <div className={styles.tags}>
          {groups.reduce((acc, group) => {
            group.forEach(tag => acc.push(
              <button
                key={tag.value}
                type='button'
                disabled={records.loading}
                className={cx(styles.tag, {
                  [styles.checked]: records?.filter?.tags?.includes(tag.value)
                })}
                onClick={() => onTag(tag.value)}
              >
                {tag.display}
              </button>
            ))
            return acc
          }, [])}
        </div>
        {total > 0 && (
          <div className='total-records'>
            {currentPage * PER_PAGE + 1}
            –
            {Math.min(total, (currentPage + 1) * PER_PAGE)}
            {' '}
            of
            {' '}
            {total}
            {' '}
            {total === 1 ? 'record' : 'records'}
          </div>
        )}
        {total === 0 && (
          <div className='total-records'>0 records</div>
        )}
      </div>

      <MagicGrid
        loading={records?.loading}
        rows={rows}
        headers={schema.headers}
        canDelete={false}
        itemName='records'
        onSort={onSort}
        sortOn={records?.sort?.on || sort?.on || 0}
        sortDir={records?.sort?.dir || sort?.dir || 'asc'}
        showCounter={false}
        ref={ref}
      />
      {(records?.error && (
        <div className='row'>
          <div className='col-xs-12'>
            <ErrorMessage errors={[ records?.error ]} />
          </div>
        </div>
      )) || (
        <div className='card-pagination-row'>
          <Pagination
            onChange={onPagination}
            totalPages={pages}
            currentPage={currentPage + 1}
          />
        </div>
      )}
    </div>
  )
}

export default SearchGrid
